import { authStore } from '@/stores/auth-store';
import type { ApiProblemBody, ApiSuccess, AuthTokens } from '@/types/api';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/**
 * Exported for the two things that cannot go through `request()`: a download the browser streams
 * to disk itself, and an upload driven by XHR because `fetch` still cannot report progress.
 */
export const apiBaseUrl = API_BASE;

/** Thrown for any non-2xx API response, carrying the parsed problem+json body. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ApiProblemBody,
  ) {
    super(problem.detail || problem.title);
    this.name = 'ApiError';
  }

  get fieldErrors(): Record<string, string[]> {
    return this.problem.errors ?? {};
  }
}

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Skip attaching the access token and the auto-refresh-on-401 behaviour. */
  skipAuth?: boolean;
}

function buildHeaders(options: RequestOptions, hasJsonBody: boolean): Headers {
  const headers = new Headers(options.headers);
  // A caller may ask for something else — a stream of events — and keeps what it asked for.
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  // Never for FormData. The browser has to set `Content-Type` itself there, because multipart
  // needs a boundary parameter only it knows — setting the header by hand produces a request the
  // server cannot parse, and the symptom is an empty $_FILES with no error anywhere.
  if (hasJsonBody) headers.set('Content-Type', 'application/json');
  headers.set('X-Request-Id', crypto.randomUUID());

  if (!options.skipAuth) {
    const token = authStore.getState().accessToken;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const problem = (body ?? {
      type: 'about:blank',
      title: 'Request failed',
      status: response.status,
      code: 'UNKNOWN',
      detail: response.statusText,
    }) as ApiProblemBody;
    throw new ApiError(response.status, problem);
  }

  return (body as ApiSuccess<T>).data;
}

// ── Silent refresh coordination ──────────────────────────────────────────────
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const refreshToken = authStore.getState().refreshToken;
  if (!refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const tokens = ((await res.json()) as ApiSuccess<AuthTokens>).data;
      authStore.getState().updateTokens(tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function doFetch(method: string, path: string, body: unknown, options: RequestOptions): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    method,
    headers: buildHeaders(options, body !== undefined && !(body instanceof FormData)),
    // FormData goes through untouched; everything else is JSON.
    body: body === undefined ? null : body instanceof FormData ? body : JSON.stringify(body),
    signal: options.signal ?? null,
    credentials: 'same-origin',
  });
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  let response = await doFetch(method, path, body, options);

  if (response.status === 401 && !options.skipAuth) {
    const { impersonation, refreshToken } = authStore.getState();

    if (impersonation !== null) {
      // An impersonation token cannot be refreshed — that is the point of it. A 401 means the
      // half-hour is up, so the administrator gets their own session back rather than being
      // signed out of the platform for having looked at a customer's account.
      //
      // The request is deliberately *not* retried: re-running a read as the administrator would
      // quietly return their data under a banner still naming the customer.
      authStore.getState().endImpersonation();
    } else if (refreshToken) {
      const refreshed = await refreshSession();
      if (refreshed) {
        response = await doFetch(method, path, body, options);
      } else {
        authStore.getState().clear();
      }
    }
  }

  return parse<T>(response);
}

/**
 * Like apiClient.get, but returns the envelope's `meta` alongside `data` — needed by
 * cursor-paginated endpoints, which carry `next_cursor` there.
 */
export async function apiFetchWithMeta<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: Record<string, unknown> }> {
  let response = await doFetch('GET', path, undefined, options);

  if (response.status === 401 && !options.skipAuth && authStore.getState().refreshToken) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await doFetch('GET', path, undefined, options);
    } else {
      authStore.getState().clear();
    }
  }

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    throw new ApiError(response.status, body as ApiProblemBody);
  }

  const envelope = body as ApiSuccess<T>;
  return { data: envelope.data, meta: envelope.meta ?? {} };
}

/**
 * A POST whose response is read as it arrives, for Juni's answers, which come as server-sent
 * events: `request()` would wait for the whole body.
 *
 * Signing in again after an expired token works as it does for `request()`. A refusal (validation,
 * a rate limit) arrives before the stream opens, as the usual problem JSON, and is thrown as an
 * `ApiError`; otherwise the open response is returned for the caller to read.
 */
export async function apiStream(path: string, body: unknown, options: RequestOptions = {}): Promise<Response> {
  const streamOptions: RequestOptions = {
    ...options,
    headers: { ...options.headers, Accept: 'text/event-stream' },
  };
  let response = await doFetch('POST', path, body, streamOptions);

  if (response.status === 401 && !options.skipAuth) {
    const { impersonation, refreshToken } = authStore.getState();

    if (impersonation !== null) {
      authStore.getState().endImpersonation();
    } else if (refreshToken) {
      if (await refreshSession()) {
        response = await doFetch('POST', path, body, streamOptions);
      } else {
        authStore.getState().clear();
      }
    }
  }

  if (!response.ok) {
    const text = await response.text();
    let problem: ApiProblemBody = {
      type: 'about:blank',
      title: 'Request failed',
      status: response.status,
      code: 'UNKNOWN',
      detail: response.statusText,
    };

    try {
      if (text !== '') problem = JSON.parse(text) as ApiProblemBody;
    } catch {
      // Not JSON: a proxy's own error page. The status still says what happened.
    }

    throw new ApiError(response.status, problem);
  }

  return response;
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  /**
   * `body` is optional and rarely used, but DELETE genuinely needs it in one place.
   *
   * Dropping a database requires a typed confirmation, and the alternative — putting the value in
   * a query string — would place the name of the thing being destroyed into access logs and
   * browser history, for the one operation somebody may later have to account for.
   */
  delete: <T>(path: string, options?: RequestOptions & { body?: unknown }) =>
    request<T>('DELETE', path, options?.body, options),
};
